// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface ScoutTaskAssignment {
  id: string;
  task_id: string;
  athlete_profile_id: string;
  assigned_by: string;
  assigned_at: string;
  due_date?: string;
  notes?: string;
  status: 'new' | 'in_progress' | 'completed';
  updates?: string;
  created_at: string;
  updated_at: string;
}

export interface AssignmentWithDetails extends ScoutTaskAssignment {
  scout_task: {
    id: string;
    title: string;
    description?: string;
    priority: 'low' | 'normal' | 'high';
  };
  athlete_profile: {
    id: string;
    name: string;
    avatar_url?: string;
    email?: string;
    username?: string;
  };
  scout_profile?: {
    id: string;
    name: string;
    email?: string;
    avatar_url?: string;
  };
}

export interface NewAssignment {
  task_id: string;
  athlete_profile_id: string;
  due_date?: string;
  notes?: string;
  status?: 'new' | 'in_progress' | 'completed';
}

export const useScoutTaskAssignments = (athleteProfileId?: string | null) => {
  const { user, profile } = useAuth();
  const [assignments, setAssignments] = useState<AssignmentWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id && profile?.id) {
      fetchAssignments();
    }
  }, [user?.id, profile?.id, athleteProfileId]);

  const fetchAssignments = async () => {
    if (!user?.id || !profile?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      let query = supabase
        .from('scout_task_assignments')
        .select(`
          *,
          scout_task:scout_tasks!inner(
            id,
            title,
            description,
            priority
          ),
          athlete_profile:athlete_profile_id(
            id,
            name,
            email,
            avatar_url,
            username
          )
        `)
        .eq('assigned_by', profile.id)
        .order('assigned_at', { ascending: false });

      // If athleteProfileId is provided, filter by it
      if (athleteProfileId) {
        query = query.eq('athlete_profile_id', athleteProfileId);
      }

      const { data, error } = await query;

      if (error) throw error;

      setAssignments(data || []);
    } catch (err) {
      console.error('Error fetching scout task assignments:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch assignments');
    } finally {
      setIsLoading(false);
    }
  };

  const createAssignment = async (assignmentData: NewAssignment): Promise<{ success: boolean; isDuplicate?: boolean; message?: string }> => {
    if (!user?.id || !profile?.id) return { success: false, message: 'User not authenticated' };

    try {
      // First check if assignment already exists
      const { data: existingAssignment } = await supabase
        .from('scout_task_assignments')
        .select('id')
        .eq('task_id', assignmentData.task_id)
        .eq('athlete_profile_id', assignmentData.athlete_profile_id)
        .eq('assigned_by', profile.id)
        .maybeSingle();

      if (existingAssignment) {
        return { 
          success: false, 
          isDuplicate: true, 
          message: 'Task already assigned to this athlete' 
        };
      }

      const { error } = await supabase
        .from('scout_task_assignments')
        .insert({
          ...assignmentData,
          assigned_by: profile.id,
          status: assignmentData.status || 'new' // Default to 'new' instead of 'in_progress'
        });

      if (error) throw error;

      await fetchAssignments(); // Refresh to get the full data with joins
      return { success: true };
    } catch (err) {
      console.error('Error creating assignment:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create assignment';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  const updateAssignment = async (
    assignmentId: string, 
    updates: Partial<Pick<ScoutTaskAssignment, 'due_date' | 'notes' | 'status' | 'updates'>>
  ): Promise<boolean> => {
    try {
      console.log('🛠 === ASSIGNMENT UPDATE START ===');
      console.log('🛠 Assignment ID:', assignmentId);
      console.log('🛠 Raw updates object:', JSON.stringify(updates, null, 2));
      console.log('🛠 Updates object type:', typeof updates);
      console.log('🛠 Updates object keys:', Object.keys(updates));
      
      // Clean the updates object to remove any undefined values
      const cleanedUpdates = Object.fromEntries(
        Object.entries(updates).filter(([_, value]) => value !== undefined)
      );
      
      const updateData = {
        ...cleanedUpdates,
        updated_at: new Date().toISOString()
      };
      
      console.log('🛠 Cleaned updates object:', JSON.stringify(cleanedUpdates, null, 2));
      console.log('🛠 Final update payload:', JSON.stringify(updateData, null, 2));
      console.log('🛠 Update payload size:', JSON.stringify(updateData).length, 'characters');

      console.log('🛠 Executing Supabase update...');
      const { data, error } = await supabase
        .from('scout_task_assignments')
        .update(updateData)
        .eq('id', assignmentId);

      if (error) {
        console.error('❌ === SUPABASE UPDATE ERROR ===');
        console.error('❌ Error object:', error);
        console.error('❌ Error code:', error.code);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error details:', error.details);
        console.error('❌ Error hint:', error.hint);
        console.error('❌ Full error JSON:', JSON.stringify(error, null, 2));
        throw error;
      }

      console.log('✅ Assignment update response:', data);
      console.log('✅ Update successful - response type:', typeof data);
      console.log('✅ === ASSIGNMENT UPDATE END ===');

      // Update local state optimistically
      setAssignments(prev => prev.map(assignment => 
        assignment.id === assignmentId 
          ? { ...assignment, ...cleanedUpdates, updated_at: new Date().toISOString() }
          : assignment
      ));
      
      return true;
    } catch (err) {
      console.error('❌ === CATCH BLOCK ERROR ===');
      console.error('❌ Caught error:', err);
      console.error('❌ Error type:', typeof err);
      console.error('❌ Error constructor:', err?.constructor?.name);
      if (err instanceof Error) {
        console.error('❌ Error message:', err.message);
        console.error('❌ Error stack:', err.stack);
      }
      setError(err instanceof Error ? err.message : 'Failed to update assignment');
      return false;
    }
  };

  const deleteAssignment = async (assignmentId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('scout_task_assignments')
        .delete()
        .eq('id', assignmentId)
        .eq('assigned_by', profile?.id);

      if (error) throw error;

      setAssignments(prev => prev.filter(assignment => assignment.id !== assignmentId));
      return true;
    } catch (err) {
      console.error('Error deleting assignment:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete assignment');
      return false;
    }
  };

  return {
    assignments,
    isLoading,
    error,
    createAssignment,
    updateAssignment,
    deleteAssignment,
    refetch: fetchAssignments
  };
};
